import { useRef } from 'react';
import { useLocation } from 'react-router';
import { useToggle } from 'ahooks';
import { useAppDisplayModeContext } from '@archipro-design/aria';
import { NoSearchResults } from '@modules/professional/component/shared/NoSearchResults';
import { SearchFilter } from '@modules/professional/component/shared/SearchFilter';
import type { DefaultElasticSearchResults } from '~/modules/professional/type';
import { CollapsiblePageLayout } from '~/modules/category/component/collapsible-page-layout/CollapsiblePageLayout';
import { useSearchFilters } from '@modules/search/hook/use-search-filters';
import { useMatchesHandleData } from '~/modules/root/hook';
import type { SearchIndex } from '~/modules/search/type';
import type { FilterStatesKey } from '~/modules/search/type/filter-state';
import { IN_PAGE_SEARCH_IGNORE_FILTER_KEYS } from '~/modules/professional/config/constants';
import { CategoryFilterNav } from '@modules/category/component/category-filter-section/CategoryFilterNav';
import AppliedFilters from '~/modules/search/components/search-filter-section/AppliedFilters';
import { resetFilterStates } from '~/modules/search/util/reset-filter-states';
import MoreProductsComing from '~/modules/products-category/component/DetailedProductSpliter';
import { useProfessionalInitialRouteData } from '../../hook/use-professional-initial-route-data';
import findLastIndex from 'lodash.findlastindex';
import type { TileViewModel } from '@rocco/ui/tile-shared/view-models/tile';
import type { ProductTileViewModel as ProductTileViewModelRocco } from '@rocco/ui/tile-product/view-models/product';
import { Grid } from '@rocco/ui/layout/components/Grid';
import { Pagination as TilePagination } from '@rocco/ui/pagination/components/Pagination';
import { buildPaginationUrl } from '~/modules-rocco/pagination/util/build-pagination-url';
import { CategoryPageTileWithAdminActions } from '~/ui-rocco/pages/category-page/CategoryPageTileWithAdminActions';
import { useCategoryTilesTracking } from '@ui-rocco/hooks/use-category-tiles-tracking';

const IGNORE_FILTER_KEYS: FilterStatesKey[] = [
    ...IN_PAGE_SEARCH_IGNORE_FILTER_KEYS,
    'searchLocation',
];

interface PaginatedProductsProp extends DefaultElasticSearchResults {
    productTilesRocco: TileViewModel<ProductTileViewModelRocco>[];
    disableTileLink?: boolean;
    disableSaveToDesignBoard?: boolean;
    disableSort?: boolean;
}

export const PaginatedProducts: React.FC<PaginatedProductsProp> = ({
    pagination,
    productTilesRocco,
    filterStates,
    disableSaveToDesignBoard,
    disableSort,
}) => {
    const professionalLoaderData = useProfessionalInitialRouteData();
    const professionalTitle = professionalLoaderData?.Title ?? '';
    const appDisplayMode = useAppDisplayModeContext();
    const { desktop } = appDisplayMode;
    const isBMPreview = useMatchesHandleData('isBMPreview', false);
    const remixLocation = useLocation();
    const [filterDrawerOpen, { toggle: toggleFilterDrawer }] =
        useToggle(!disableSort);
    const gridColumns = desktop ? (filterDrawerOpen ? 4 : 5) : 2;
    const gridRef = useRef<HTMLDivElement | null>(null);
    useCategoryTilesTracking(gridRef);

    const tiles = productTilesRocco.map((tile) => {
        return (
            <div
                key={tile.model.id}
                className="transform-gpu transition-transform duration-300"
            >
                <CategoryPageTileWithAdminActions
                    tile={{
                        ...tile,
                        section: 'ProfessionalProducts',
                        disableDesignBoard:
                            isBMPreview || disableSaveToDesignBoard,
                    }}
                />
            </div>
        );
    });

    if (filterStates.isPurchasable?.checked) {
        const lastPurchasbaleItemIndex = findLastIndex(
            productTilesRocco,
            (t) => t.model.canPurchase,
        );
        const purchasableCount =
            productTilesRocco?.filter((t) => t.model.canPurchase).length ?? 0;
        if (
            purchasableCount === 0 ||
            (lastPurchasbaleItemIndex !== undefined &&
                lastPurchasbaleItemIndex !== -1 &&
                lastPurchasbaleItemIndex !== productTilesRocco.length - 1)
        ) {
            const moreProductsComing = (
                <MoreProductsComing
                    key="MoreProductsComing"
                    professionalTitle={professionalTitle}
                    columns={gridColumns}
                />
            );
            tiles.splice(lastPurchasbaleItemIndex + 1, 0, moreProductsComing);
        }
    }

    const category = useMatchesHandleData<SearchIndex>('category', 'project');

    const searchFilters = useSearchFilters({
        category,
        initialStates: filterStates,
        isCategoryPage: false,
        ignoreFilterKeys: IGNORE_FILTER_KEYS,
    });

    return (
        <div>
            <div className="mx-12.5 hidden h-[1px] bg-black md:block qhd:px-0"></div>

            <div className="md:mt-16 md:pb-47.5">
                <CollapsiblePageLayout
                    sidePanel={
                        desktop && (
                            <div className="md:pt-15">
                                <CategoryFilterNav
                                    category="product"
                                    searchFilters={searchFilters}
                                    hideCategorySectionTitle={false}
                                />
                            </div>
                        )
                    }
                    hideSidePanel={!filterDrawerOpen}
                >
                    <div className="md:h-15">
                        <SearchFilter
                            searchFilters={searchFilters}
                            filterDrawerOpen={filterDrawerOpen}
                            toggleFilterDrawer={toggleFilterDrawer}
                            disableSort={disableSort}
                            disableToggleFilter={true}
                        />
                    </div>
                    {searchFilters.appliedFilters &&
                        searchFilters.appliedFilters.length > 0 && (
                            <AppliedFilters
                                filterItems={searchFilters.appliedFilters}
                                resetFilters={() =>
                                    searchFilters.resetFilters(
                                        resetFilterStates(
                                            searchFilters.filterStates,
                                            [...IGNORE_FILTER_KEYS, 'category'],
                                        ),
                                    )
                                }
                            />
                        )}
                    <div>
                        {!tiles.length ? (
                            <NoSearchResults resultsType={'products'} />
                        ) : (
                            <>
                                <Grid
                                    ref={gridRef}
                                    colClasses={
                                        filterDrawerOpen
                                            ? 'grid-cols-2 lg:grid-cols-4'
                                            : 'grid-cols-2 lg:grid-cols-4'
                                    }
                                    rowsGapClass="gap-x-2 gap-y-12 md:gap-x-6 md:gap-y-20"
                                >
                                    {tiles}
                                </Grid>

                                {pagination.totalPages > 1 && (
                                    <div className="pt-[6.5625rem] md:pt-20">
                                        <TilePagination
                                            currentPath={remixLocation}
                                            current={pagination.page}
                                            total={pagination.totalPages}
                                            urlBuilder={buildPaginationUrl}
                                            visibleCount={
                                                appDisplayMode.desktop ? 4 : 3
                                            }
                                        />
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </CollapsiblePageLayout>
            </div>
        </div>
    );
};
